import Vue from 'vue';
import tokenExpirationService from '@/api/token-expiration-service';
import eventBus, { OPEN_SNACKBAR } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { mapErrorsToInputs } from '@/util/forms';
import { getLocalStorageItem, setLocalStorageItem } from '@/util/local-storage';

// setup user activity tracking
// putting it here because this module handles inactive user auto logout logic
let expirationIntervalId;
let lastActivityTime = new Date().getTime();
let activityTimeLastStoredAt = new Date().getTime();

export const setLastActivityTime = () => {
  lastActivityTime = new Date().getTime();
  if (lastActivityTime - activityTimeLastStoredAt > 10000) {
    // throttling the local storage writing,
    // because this function can be called very frequently while scrolling
    storeLastActivityTime();
  }
};
const storeLastActivityTime = () => {
  // storing last activity time in local storage to keep it synced between open browser tabs.
  const storedLastActivityTime = Number(getLocalStorageItem('lastActivityTime'));
  if (storedLastActivityTime >= lastActivityTime) {
    lastActivityTime = storedLastActivityTime;
  } else {
    setLocalStorageItem('lastActivityTime', lastActivityTime);
  }
  activityTimeLastStoredAt = new Date().getTime();
};
window.onmousedown = setLastActivityTime;
window.onkeydown = setLastActivityTime;
window.ontouchstart = setLastActivityTime;
window.onscroll = setLastActivityTime;
// window.onmousemove = setLastActivityTime;

const state = {
  tokenExpiration: null,
  newTokenExpiration: {},
  tokenExpirationValidationErrors: {},
};

const getters = {};

const mutations = {
  SET_NEW_TOKEN_EXPIRATION(state, tokenExpiration) {
    state.newTokenExpiration = tokenExpiration;
  },

  SET_TOKEN_EXPIRATION(state, data) {
    state.tokenExpiration = data;
  },

  UPDATE_TOKEN_EXPIRATION(state, tokenExpiration) {
    state.tokenExpiration = tokenExpiration;
  },

  SET_TOKEN_EXPIRATION_VALIDATION_ERRORS(state, tokenExpirationValidationErrors) {
    state.tokenExpirationValidationErrors = tokenExpirationValidationErrors;
  },

  CLEAR_TOKEN_EXPIRATION_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.tokenExpirationValidationErrors, field);
  },
};

const actions = {
  // fetches token expiration time instantly and every minute
  // logs out the user if they have been inactive for longer than expiration time
  checkTokenExpiration({ commit, dispatch }) {
    const checkExpiration = async () => {
      const { data } = await tokenExpirationService.get();
      commit('SET_TOKEN_EXPIRATION', data); // fills system settings form

      const currentTime = new Date().getTime();
      const lastActivityTimeComputed =
        Number(getLocalStorageItem('lastActivityTime')) || lastActivityTime;

      // refresh_token_expire is in minutes, time is in milliseconds
      if (currentTime - lastActivityTimeComputed > data.refresh_token_expire * 60000) {
        dispatch('auth/logout', null, { root: true });
        eventBus.$emit(OPEN_SNACKBAR, {
          text: i18n.t('session_expired'),
          timeout: 10000,
        });
      }

      storeLastActivityTime();
    };

    checkExpiration();

    clearInterval(expirationIntervalId);
    expirationIntervalId = setInterval(checkExpiration, 60000);
  },

  stopCheckingTokenExpiration() {
    clearInterval(expirationIntervalId);
  },

  updateTokenExpiration({ commit }, tokenExpiration) {
    return tokenExpirationService
      .update(tokenExpiration)
      .then((res) => {
        commit('SET_NEW_TOKEN_EXPIRATION', {});
        commit('UPDATE_TOKEN_EXPIRATION', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('token_expiration_updated'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_TOKEN_EXPIRATION_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  clearTokenExpirationValidationErrors({ commit }, field) {
    commit('CLEAR_TOKEN_EXPIRATION_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
